import React, {ReactElement} from 'react';
import {IValidationErrorObject} from '../../../../incase-domain/common/validationErrorResponse';
import {TABS} from '../../form/part/tabs';
import ElementShadow from '../common/elementShadow';
import {COLOR_FIELDS} from '../common/fontAndBackgroundColorPicker';
import ChangeAnswer from './answers/actions/ChangeAnswer';
import AnswerActions, {AnswerActionType} from './answers/AnswerActions';
import Answers from './answers/Answers';
import AdvancedOptions from './components/advancedOptions/AdvancedOptions';
import QuestionOptions, {QUESTION_OPTIONS} from './components/QuestionOptions';
import QuestionText from './components/QustionText';
import {IElementQuestionYesNoV2} from '../../../../incase-domain/elements/question/questionYesNoV2';
import {usePresentationLabels} from '../../../../hooks/usePresentationLabels';
import {useHighlightedElement, useSetHighlightedElement} from '../../../../utils/hooks/slideUpdates/useSetSlideElement';
import AnimationForm from '../common/animationForm';

function answerLabelCode(text: string): 'elements-yesnoquestion-yes' | 'elements-yesnoquestion-no' {
    return text.toLowerCase() === 'no' ? 'elements-yesnoquestion-no' : 'elements-yesnoquestion-yes';
}

interface HighlightedQuestionElementComponentProps {
    errors: IValidationErrorObject | null;
    activeTab: string;
}

function HighlightedQuestionYesNoV2ElementComponent(props: HighlightedQuestionElementComponentProps): ReactElement {
    const {errors, activeTab} = props;
    const getLabel = usePresentationLabels();
    const updateHighlightedElement = useSetHighlightedElement();
    const highlightedElement = useHighlightedElement() as IElementQuestionYesNoV2;

    if (activeTab === TABS.STYLE.id) {
        return (
            <>
                <AdvancedOptions
                    highlightedElement={highlightedElement}
                    highlightedElementChangeHandler={updateHighlightedElement}
                    answerColorFields={[COLOR_FIELDS.CORRECT_COLOR, COLOR_FIELDS.INCORRECT_COLOR, COLOR_FIELDS.DEBATABLE_COLOR]}
                    commentColorFields={[
                        COLOR_FIELDS.CORRECT_COMMENT_COLOR,
                        COLOR_FIELDS.INCORRECT_COMMENT_COLOR,
                        COLOR_FIELDS.DEBATABLE_COMMENT_COLOR,
                    ]}
                />
                <ElementShadow highlightedElement={highlightedElement} highlightedElementChangeHandler={updateHighlightedElement} />
            </>
        );
    }

    if (activeTab === TABS.ADVANCED.id) {
        return (
            <>
                <QuestionOptions
                    highlightedElement={highlightedElement}
                    highlightedElementChangeHandler={updateHighlightedElement}
                    questionOptions={[
                        QUESTION_OPTIONS.BLOCKING_QUESTION,
                        QUESTION_OPTIONS.SHOW_IN_REPORT,
                        QUESTION_OPTIONS.SHOW_RESULTS,
                        QUESTION_OPTIONS.SHOW_ALL_COMMENTS,
                    ]}
                />
                <AnimationForm
                    appearAfter={highlightedElement.appearAfter || 0}
                    elementAnimations={highlightedElement.elementAnimations}
                    highlightedElementChangeHandler={updateHighlightedElement}
                />
            </>
        );
    }

    return (
        <>
            <QuestionText
                highlightedElement={highlightedElement}
                errors={errors}
                highlightedElementChangeHandler={updateHighlightedElement}
                showDebatable
            />
            <Answers errors={errors}>
                {highlightedElement.answers.map((answer, index) => (
                    <React.Fragment key={index}>
                        <ChangeAnswer
                            answer={getLabel(answerLabelCode(answer.text)) || answer.text}
                            validationErrors={errors}
                            index={index}
                            highlightedElement={highlightedElement}
                            highlightedElementChangeHandler={updateHighlightedElement}
                        />
                        <AnswerActions
                            index={index}
                            validationErrors={errors}
                            highlightedElement={highlightedElement}
                            highlightedElementChangeHandler={updateHighlightedElement}
                            actions={[AnswerActionType.CHANGE_COMMENT, AnswerActionType.ANSWER_RULES]}
                        />
                    </React.Fragment>
                ))}
            </Answers>
        </>
    );
}

export default HighlightedQuestionYesNoV2ElementComponent;
